import { DSChannel, DSContent, DSContentGroup, DSPlayerOBj, PlayerComputedContentOrContentGroup } from "./types";
import _ from "lodash";

const getContentOrContentGroupFromItem = (item: {
  content?: DSContent<Date>;
  contentGroup?: DSContentGroup<Date>;
}): (PlayerComputedContentOrContentGroup | undefined)[] | undefined => {
  if (item.content) {
    return [item.content];
  }

  return item.contentGroup ? [item.contentGroup] : undefined;
};

type PrioResult = {
  contentOrContentGroup: (PlayerComputedContentOrContentGroup | undefined)[] | undefined;
  // Used to fallback to player, or when showing audio
  fallBackContent?: DSPlayerOBj<Date>["playerDefaultContent"];
  extra?: any;
  source:
    | "content"
    | "contentGroup"
    | "channel"
    | "channelDefault"
    | "campaign"
    | "campaignDefault"
    | "area"
    | "areaDefaultContent"
    | "playerDefaultContent";
};

const filterCompatibleFromDetails = (detail: DSChannel<Date>["details"][0], playerScreenSizeId: string) => {
  return detail.detailContents.some((detailContent) => {
    if (detailContent.content) {
      return detailContent.content?.screenSizeId === playerScreenSizeId;
    }
    if (detailContent.contentGroup) {
      return detailContent.contentGroup.contentDetials
        .filter((r) => r.isActive)
        .some((member) => {
          return member.screenSizeId === playerScreenSizeId;
        });
    }
    return false;
  });
};

const getPlayerChannelContentsWithoutCombining = (playerChannel: DSChannel<Date>, playerScreenSizeId: string) => {
  const playerChannelFiltered = playerChannel.details.filter((detail) =>
    filterCompatibleFromDetails(detail, playerScreenSizeId)
  );

  if (playerChannelFiltered.length > 0 && playerChannelFiltered[0].detailContents.length > 0) {
    const con = playerChannelFiltered[0].detailContents
      .map((detailContent) => {
        return getContentOrContentGroupFromItem(detailContent);
      })
      .flat()
      .filter(Boolean) as PlayerComputedContentOrContentGroup[];
    return con;
  }

  return [];
};

// Area > campaaign > (channel | contentGroup | content) > playerDefaultContent
export const getTheHighestPriorityItemOrShuffled = (player: DSPlayerOBj<Date>): PrioResult => {
  const playerScreenSizeId = player.playerScreenSizeId;
  const np: DSPlayerOBj<Date> = player;

  if (player.areaOverwriteSub) {
    const areaContentList = np.areaContentList || [];
    let areaContentListFiltered = areaContentList.filter((areaContent) => {
      if (areaContent.content) {
        return areaContent.content?.screenSizeId === playerScreenSizeId;
      }
      if (areaContent.contentGroup) {
        return areaContent.contentGroup.contentDetials
          .filter((r) => r.isActive)
          .some((member) => {
            return member.screenSizeId === playerScreenSizeId;
          });
      }
    });

    if (areaContentListFiltered.length > 0) {
      return {
        contentOrContentGroup: getContentOrContentGroupFromItem(
          areaContentListFiltered[0]
        ) as PlayerComputedContentOrContentGroup[],
        source: "area",
        extra: {
          area: {
            list: np.areaContentList,
            name: np.areaId,
          },
        },
        fallBackContent: np.playerDefaultContent,
      };
    }
    return {
      contentOrContentGroup: [np.areaDefaultContent],
      source: "areaDefaultContent",
      extra: np,
      fallBackContent: np.playerDefaultContent,
    };
  }

  if (np.playerCampaigns && np.playerCampaigns.length > 0) {
    const playerCampaigns = np.playerCampaigns || [];
    const playerCampaignsFiltered = playerCampaigns.filter((playerCampaign) => {
      // We check [0] because campaign details can only be empty or 1 item in array
      return filterCompatibleFromDetails(playerCampaign.details[0], playerScreenSizeId);
    });

    if (playerCampaignsFiltered.length > 0) {
      let conCombined: PlayerComputedContentOrContentGroup[] = [];
      for (const camp of playerCampaignsFiltered) {
        const con = camp.details[0].detailContents
          .map((detailContent) => {
            return getContentOrContentGroupFromItem(detailContent);
          })
          .flat()
          .filter(Boolean) as PlayerComputedContentOrContentGroup[];

        conCombined.push(...con);

        if (camp.isCombineAndShuffle || np.playerChannel?.isCombineAndShuffle) {
          if (player.playerContent) {
            conCombined.push(player.playerContent);
          }
          if (player.playerContentGroup) {
            conCombined.push(player.playerContentGroup);
          }
          if (player.playerChannel) {
            conCombined.push(...getPlayerChannelContentsWithoutCombining(player.playerChannel, playerScreenSizeId));
          }
          conCombined = _.shuffle(conCombined);
        }
      }
      return {
        contentOrContentGroup: conCombined,
        source: "campaign",
        extra: playerCampaignsFiltered,
        fallBackContent: np.playerDefaultContent,
      };
    }

    if (playerCampaigns[0]?.defaultContent?.contentintId) {
      return {
        contentOrContentGroup: [playerCampaigns[0].defaultContent],
        source: "campaignDefault",
        extra: playerCampaigns,
        fallBackContent: np.playerDefaultContent,
      };
    }
  }

  if (np.playerChannel) {
    const playerChannel = np.playerChannel;
    const playerChannelFiltered = playerChannel.details.filter((detail) =>
      filterCompatibleFromDetails(detail, playerScreenSizeId)
    );

    if (playerChannelFiltered.length > 0 && playerChannelFiltered[0].detailContents.length > 0) {
      return {
        contentOrContentGroup: getPlayerChannelContentsWithoutCombining(playerChannel, playerScreenSizeId),
        source: "channel",
        extra: playerChannel,
        fallBackContent: np.playerDefaultContent,
      };
    }

    if (playerChannel.defaultContent?.contentintId) {
      return {
        contentOrContentGroup: [playerChannel.defaultContent],
        source: "channelDefault",
        extra: playerChannel,
        fallBackContent: np.playerDefaultContent,
      };
    }
  }

  if (np.playerContentGroup) {
    return {
      contentOrContentGroup: [np.playerContentGroup],
      source: "contentGroup",
      fallBackContent: np.playerDefaultContent,
    };
  }

  if (np.playerContent) {
    return {
      contentOrContentGroup: [np.playerContent],
      source: "content",
      fallBackContent: np.playerDefaultContent,
    };
  }

  return {
    contentOrContentGroup: [np.playerDefaultContent],
    source: "playerDefaultContent",
    extra: np,
    fallBackContent: np.playerDefaultContent,
  };
};
